import React, { useCallback, useEffect, useState } from 'react';

import { router, usePage } from "@inertiajs/react";

import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@heroui/modal";
import { DatePicker } from "@heroui/react";
import { Select, SelectItem } from "@heroui/select";


import { Button, useDisclosure } from "@heroui/react";
import { I18nProvider } from "@react-aria/i18n";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "react-i18next";


import { RoomsType } from '@/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { AnimatePresence, motion } from 'framer-motion';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

type Props = {
    title: string
}

type FormData = {
    email: string
    lastname: string
    message: string
    name: string
    phone: string
    checkin: string
    checkout: string
    room: string
}

export const ErrorMessage = ({ msg }: { msg: string | undefined }) => {

    return (
        <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}

            className="text-red-500 md:absolute top-1 left-2 text-[10px] "
        >
            {msg}
        </motion.div>
    )

}

const BookModal: React.FC<Props> = ({ title }) => {

    const { props } = usePage()
    const { executeRecaptcha } = useGoogleReCaptcha()
    const { t } = useTranslation(["validation", "glossary"])
    const { isOpen, onOpen, onOpenChange } = useDisclosure()

    const [selectData, setSelectData] = useState<RoomsType[]>()
    const [loading, setLoading] = useState<boolean>(false)

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t("error-name")),
        lastname: Yup.string().required(t("error-lastname")),
        email: Yup.string().email(t("error-email")).required(t("required-email")),
        phone: Yup.number().required(t("error-phone")).typeError(t("type-number")),
        message: Yup.string().required(t("error-message")),
        checkin: Yup.string().required(t("error-checkin")),
        checkout: Yup.string().required(t("error-checkout")),
        room: Yup.string(),
    })

    const { register, control, handleSubmit, reset, setError, clearErrors, formState: { errors, isSubmitting } } = useForm<FormData>({
        resolver: yupResolver(validationSchema)
    })

    const onSubmit = useCallback(
        (data: FormData, e: SubmitEvent) => {

            e.preventDefault();
            if (!executeRecaptcha) {
                console.log("Execute recaptcha not yet available");
                return;
            }
            executeRecaptcha("form").then((gReCaptchaToken: string) => {
                submitForm(gReCaptchaToken, data);
            });
        },
        [executeRecaptcha]
    );

    const submitForm = (gReCaptchaToken: string, data: FormData) => {

        const finalData = {
            ...data,
            "g-recaptcha-response": gReCaptchaToken
        }

        router.post(
            `/${props.locale}/${props.accomodation.data.slug.prefix}/${props.accomodation.data.slug.key}/book-now`,
            finalData,
            {
                preserveState: true,
                onStart: () => {
                    setLoading(true)
                },
                onSuccess: () => {
                    reset()
                    onOpenChange()
                },
                onFinish: () => {
                    setLoading(false)
                },
                onError: (errors) => {

                },
            },
        );
    };



    useEffect(() => {

        if (isOpen) {
            router.reload({
                only: ["rooms"],
                onSuccess: (page) => {

                    if (page.props.rooms.data.length != 0) {
                        setSelectData(page.props.rooms.data)
                    }
                },
                onFinish: () => {

                },
            });
        }

    }, [isOpen])

    return (
        <>
            <Button
                title={t("book_now", { ns: "glossary" })}
                endContent={<svg
                    className="size-9 relative left-4 text-gray-800 bg-white rounded-full dark:text-white"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m9 5 7 7-7 7"
                    />
                </svg>}
                onPress={onOpen}
                className="border-2 border-white text-white text-lg pl-6 rounded-3xl flex items-center justify-center gap-6 bg-transparent hover:bg-white hover:text-black transition">
                {t("book_now", { ns: "glossary" })}
            </Button>
            <Modal onClose={reset} size='4xl' isOpen={isOpen} onOpenChange={onOpenChange}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1 text-center text-2xl font-bold">{title}</ModalHeader>
                            <ModalBody className="lg:px-20 max-lg:max-h-[70vh] max-lg:overflow-scroll">

                                <div className="flex flex-col lg:flex-row gap-5 w-full">

                                    <I18nProvider locale={props.locale === "el" ? "el-GR" : "en-GB"}>
                                        <Controller

                                            name="checkin"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    variant='bordered'
                                                    classNames={{
                                                        inputWrapper: 'border-1 border-primary rounded-xl',
                                                        errorMessage: 'text-red-500 text-[10px] ml-1.5',
                                                    }}
                                                    isInvalid={!!errors.checkin}
                                                    isRequired
                                                    onChange={(date) => {
                                                        const formattedDate = `${date.day}/${date.month}/${date.year}`;
                                                        field.onChange(formattedDate);
                                                        clearErrors('checkin');
                                                    }}
                                                    errorMessage={errors.checkin?.message}
                                                    label="Check In"
                                                />


                                            )
                                            }

                                        />

                                        <Controller

                                            name="checkout"
                                            control={control}
                                            render={({ field }) => (
                                                <DatePicker
                                                    variant='bordered'
                                                    classNames={{
                                                        inputWrapper: 'border-1 border-primary rounded-xl',
                                                        errorMessage: 'text-red-500 text-[10px] ml-1.5',
                                                    }}
                                                    isInvalid={!!errors.checkout}
                                                    isRequired
                                                    onChange={(date) => {
                                                        const formattedDate = `${date.day}/${date.month}/${date.year}`;
                                                        field.onChange(formattedDate);
                                                        clearErrors('checkout');
                                                    }}
                                                    errorMessage={errors.checkin?.message}
                                                    label="Check Out"
                                                />
                                            )}
                                        />

                                    </I18nProvider>

                                </div>

                                <AnimatePresence>
                                    <form
                                        onSubmit={handleSubmit(onSubmit)}
                                        className="placeholder-black flex-grow"
                                    >
                                        <div className="flex flex-column lg:!flex-row gap-5 mt-6">
                                            <div className="flex-grow relative">
                                                <input
                                                    type="text"

                                                    {...register('name')}

                                                    placeholder={t('name') + ' *'}
                                                    className="px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black"
                                                />
                                                {errors.name && <ErrorMessage msg={errors.name.message} />}
                                            </div>
                                            <div className="flex-grow relative">
                                                <input
                                                    type="text"
                                                    {...register('lastname')}
                                                    placeholder={t('lastname') + ' *'}
                                                    className="px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black"
                                                />
                                                {errors.lastname && <ErrorMessage msg={errors.lastname.message} />}
                                            </div>
                                        </div>
                                        <div className="flex flex-column lg:!flex-row gap-5 mt-6">
                                            <div className="flex-grow relative">
                                                <input
                                                    type="email"
                                                    {...register('email')}
                                                    placeholder="Email *"
                                                    className="px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black"
                                                />
                                                {errors.email && <ErrorMessage msg={errors.email.message} />}
                                            </div>
                                            <div className="flex-grow relative">
                                                <input
                                                    type="text"
                                                    {...register('phone')}
                                                    placeholder={t('phone') + ' *'}
                                                    className={`px-2 pt-3 h-[50px] w-full rounded-xl border-primary placeholder-black`}
                                                />
                                                {errors.phone && <ErrorMessage msg={errors.phone.message} />}
                                            </div>
                                        </div>

                                        {selectData &&
                                            <Controller
                                                name="room"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select

                                                        isInvalid={!!errors.room}
                                                        onChange={(e) => {

                                                            field.onChange(e);
                                                            clearErrors('room');
                                                        }}
                                                        label={errors.room && errors.room.message}
                                                        placeholder={t('choose-room')}
                                                        className='mt-6 bg-transparent'
                                                        classNames={{
                                                            trigger: 'bg-white text-black border-primary border-1 h-[50px]',
                                                            label: 'text-black',
                                                            value: '!text-black'
                                                        }}
                                                    >
                                                        {selectData.map((item) => (
                                                            <SelectItem key={item.name}>
                                                                {item.name}
                                                            </SelectItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />

                                        }
                                        <div className="mt-6 flex-grow relative">
                                            <textarea
                                                rows={8}
                                                {...register('message')}
                                                placeholder={t('message')}
                                                className="px-2 pt-3 w-full rounded-xl border-primary placeholder-black"
                                            />
                                            {errors.message && <ErrorMessage msg={errors.message.message} />}
                                        </div>

                                        <ModalFooter className='px-0'>
                                            <Button isLoading={loading} title={t("submit", { ns: "glossary" })} className='w-full' type='submit' color="primary">
                                                {t("submit", { ns: "glossary" })}
                                            </Button>
                                        </ModalFooter>

                                    </form>
                                </AnimatePresence>

                            </ModalBody>

                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    )
}

export default BookModal


